import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from '../../../app/LanguageContext';
import { usePageTitle } from '../../../layout/PageTitleContext';
import { CustomerAutocomplete } from '../../../modules/customers/public';
import ConsignmentSettingsGuard from '../private/ConsignmentSettingsGuard';
import ConsignmentStatusChip from '../private/ConsignmentStatusChip';
import ConsignmentItemForm from '../private/ConsignmentItemForm';
import ConsignmentItemsTable from '../private/ConsignmentItemsTable';
import ConsignmentAllocateDialog from '../private/ConsignmentAllocateDialog';
import ConsignmentCloseDialog from '../private/ConsignmentCloseDialog';
import ConsignmentChangeLogTimeline from '../private/ConsignmentChangeLogTimeline';
import {
  allocateConsignment,
  createConsignment,
  finalizeConsignment,
  getConsignment,
  renewConsignment,
  updateConsignment,
} from '../private/consignmentApi';

const DRAFT_CUSTOMER_NAME = '—';
const HEADER_AUTOSAVE_MS = 900;

const emptyHeaderForm = () => ({
  customer_id: '',
  customer_name: '',
  observations: '',
  next_visit_date: '',
});

function buildHeaderPayload(form) {
  const customerName = (form.customer_name || '').trim();
  return {
    customer_id: form.customer_id,
    customer_name: customerName || DRAFT_CUSTOMER_NAME,
    customer_email: '',
    customer_phone: '',
    customer_document: '',
    customer_address: '',
    store_name: '',
    commercial_contact: '',
    observations: form.observations,
    internal_notes: '',
    next_visit_date: form.next_visit_date || '',
    next_visit_priority: 'MEDIUM',
    next_visit_frequency_days: 0,
    next_visit_notes: '',
  };
}

function mergeNotes(observations, internalNotes) {
  const parts = [observations, internalNotes].map((s) => (s || '').trim()).filter(Boolean);
  return parts.join('\n\n');
}

function displayCustomerName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed || trimmed === DRAFT_CUSTOMER_NAME) {
    return '';
  }
  return trimmed;
}

function ConsignmentPage() {
  const { translate } = useTranslation();
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const creatingRef = useRef(false);
  const autoKickoffRef = useRef(false);
  const headerSaveTimerRef = useRef(null);

  const [consignmentId, setConsignmentId] = useState(routeId || null);
  const [headerForm, setHeaderForm] = useState(emptyHeaderForm());
  const [consignment, setConsignment] = useState(null);
  const [items, setItems] = useState([]);
  const [changeLog, setChangeLog] = useState([]);
  const [loading, setLoading] = useState(!!routeId);
  const [creating, setCreating] = useState(!routeId);
  const [createError, setCreateError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [closeDialog, setCloseDialog] = useState({ open: false, mode: 'finalize' });
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const status = consignment?.status || 'PREPARATION';
  const isClosed = ['CLOSED', 'CANCELLED', 'RENEWED'].includes(status);
  const isEditable = status === 'PREPARATION' || status === 'ACTIVE';
  const canShowItemForm = isEditable && !!consignmentId;

  const loadDetail = useCallback(async () => {
    if (!consignmentId) return;
    setLoading(true);
    try {
      const resp = await getConsignment(consignmentId);
      const d = resp?.data;
      const c = d?.consignment || null;
      setConsignment(c);
      setItems(d?.items || []);
      setChangeLog(d?.change_log || []);
      if (c) {
        setHeaderForm({
          customer_id: c.customer_id || '',
          customer_name: displayCustomerName(c.customer_name),
          observations: mergeNotes(c.observations, c.internal_notes),
          next_visit_date: c.next_visit_date || '',
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [consignmentId]);

  const createConsignmentAutomatically = useCallback(async () => {
    if (creatingRef.current || routeId) return false;
    creatingRef.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const resp = await createConsignment(buildHeaderPayload(emptyHeaderForm()));
      const newId = resp?.data?.id;
      if (!newId) {
        throw new Error(translate('consignment.create_failed'));
      }
      navigate(`/consignments/${newId}`, { replace: true });
      return true;
    } catch (e) {
      setCreateError(e.message || translate('consignment.create_failed'));
      return false;
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }, [routeId, navigate, translate]);

  useEffect(() => {
    if (routeId) return;
    if (autoKickoffRef.current) return;
    autoKickoffRef.current = true;
    void createConsignmentAutomatically();
  }, [routeId, createConsignmentAutomatically]);

  useEffect(() => {
    if (consignmentId) {
      loadDetail();
    }
  }, [consignmentId, loadDetail]);

  useEffect(() => {
    if (routeId && routeId !== consignmentId) {
      setConsignmentId(routeId);
    }
  }, [routeId, consignmentId]);

  useEffect(() => {
    const title = displayCustomerName(consignment?.customer_name) || translate('consignment.new_consignment');
    setPageTitle(title);
  }, [consignment?.customer_name, setPageTitle, translate]);

  useEffect(() => () => {
    if (headerSaveTimerRef.current) {
      clearTimeout(headerSaveTimerRef.current);
    }
  }, []);

  const persistHeader = useCallback(async (form) => {
    if (!consignmentId) return;
    try {
      await updateConsignment(consignmentId, buildHeaderPayload(form));
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  }, [consignmentId]);

  const scheduleHeaderSave = useCallback((form) => {
    if (!consignmentId) return;
    if (headerSaveTimerRef.current) {
      clearTimeout(headerSaveTimerRef.current);
    }
    headerSaveTimerRef.current = setTimeout(() => {
      void persistHeader(form);
    }, HEADER_AUTOSAVE_MS);
  }, [consignmentId, persistHeader]);

  const handleHeaderChange = (patch) => {
    setHeaderForm((prev) => {
      const next = { ...prev, ...patch };
      scheduleHeaderSave(next);
      return next;
    });
  };

  const flushHeaderSave = useCallback(async (form) => {
    if (headerSaveTimerRef.current) {
      clearTimeout(headerSaveTimerRef.current);
      headerSaveTimerRef.current = null;
    }
    await persistHeader(form);
  }, [persistHeader]);

  const handleOpenAllocate = () => {
    if (!headerForm.customer_id?.trim()) {
      setSnackbar({ open: true, message: translate('quotes.customer_required'), severity: 'error' });
      return;
    }
    setAllocateOpen(true);
  };

  const handleAllocate = async () => {
    setActionLoading(true);
    try {
      await flushHeaderSave(headerForm);
      await allocateConsignment(consignmentId, {
        next_visit_date: headerForm.next_visit_date || '',
      });
      setAllocateOpen(false);
      setSnackbar({ open: true, message: translate('consignment.allocated'), severity: 'success' });
      loadDetail();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseCycle = async (payload) => {
    setActionLoading(true);
    try {
      const fn = closeDialog.mode === 'renew' ? renewConsignment : finalizeConsignment;
      const resp = await fn(consignmentId, payload);
      const data = resp?.data || {};
      setCloseDialog({ open: false, mode: closeDialog.mode });
      let message = translate('consignment.finalized');
      if (data.generated_order_id) {
        message = `${translate('consignment.finalized_with_order')} ${data.generated_order_id}`;
      }
      if (data.new_consignment_id) {
        message = `${translate('consignment.renewed_with_new')} ${data.new_consignment_id}`;
        navigate(`/consignments/${data.new_consignment_id}`);
        setConsignmentId(data.new_consignment_id);
      } else {
        loadDetail();
      }
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (!routeId && creating) {
    return (
      <ConsignmentSettingsGuard>
        <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </ConsignmentSettingsGuard>
    );
  }

  if (!routeId && createError) {
    return (
      <ConsignmentSettingsGuard>
        <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Typography color="error">{createError}</Typography>
          <Button
            variant="contained"
            onClick={() => {
              autoKickoffRef.current = false;
              void createConsignmentAutomatically();
            }}
          >
            {translate('orders.new_order_retry')}
          </Button>
        </Box>
      </ConsignmentSettingsGuard>
    );
  }

  if (loading && !consignment && routeId) {
    return (
      <ConsignmentSettingsGuard>
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </ConsignmentSettingsGuard>
    );
  }

  if (routeId && !loading && !consignment) {
    return (
      <ConsignmentSettingsGuard>
        <Box p={4}>
          <Typography color="text.secondary">{translate('consignment.not_found')}</Typography>
        </Box>
      </ConsignmentSettingsGuard>
    );
  }

  return (
    <ConsignmentSettingsGuard>
      <Box p={2}>
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/consignments')} size="small">
            {translate('common.back')}
          </Button>
          <Typography variant="h6" flex={1}>
            {displayCustomerName(consignment?.customer_name) || translate('consignment.new_consignment')}
          </Typography>
          {consignment && <ConsignmentStatusChip status={consignment.status} />}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {translate('consignment.section_consignment')}
              </Typography>

              {isEditable ? (
                <>
                  <CustomerAutocomplete
                    value={headerForm.customer_id}
                    onChange={(customerId, customer) => {
                      handleHeaderChange({
                        customer_id: customerId,
                        customer_name: customer?.name || '',
                      });
                    }}
                    onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
                    label={translate('consignment.select_customer')}
                    disabled={status !== 'PREPARATION'}
                  />
                  <TextField
                    label={translate('consignment.next_visit_date')}
                    type="date"
                    value={headerForm.next_visit_date}
                    onChange={(e) => handleHeaderChange({ next_visit_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                    helperText={translate('consignment.next_visit_date_optional')}
                  />
                  <TextField
                    label={translate('consignment.observations')}
                    value={headerForm.observations}
                    onChange={(e) => handleHeaderChange({ observations: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </>
              ) : (
                <>
                  <Typography variant="body2">{displayCustomerName(consignment?.customer_name) || '—'}</Typography>
                  {consignment?.next_visit_date && (
                    <Typography variant="body2" color="text.secondary">
                      {translate('consignment.next_visit_date')}: {consignment.next_visit_date}
                    </Typography>
                  )}
                  {consignment?.observations && (
                    <Typography variant="body2" color="text.secondary">{consignment.observations}</Typography>
                  )}
                </>
              )}

              {consignment?.generated_order_id && (
                <Typography variant="caption" color="text.secondary">
                  {translate('consignment.generated_order')}:{' '}
                  <RouterLink to={`/orders/${consignment.generated_order_id}`}>
                    {consignment.generated_order_id}
                  </RouterLink>
                </Typography>
              )}

              {consignment?.renewed_to_consignment_id && (
                <Typography variant="caption" color="text.secondary">
                  {translate('consignment.renewed_to')}:{' '}
                  <RouterLink to={`/consignments/${consignment.renewed_to_consignment_id}`}>
                    {consignment.renewed_to_consignment_id}
                  </RouterLink>
                </Typography>
              )}
            </Paper>

            {canShowItemForm && (
              <ConsignmentItemForm
                consignmentId={consignmentId}
                status={status}
                editingItem={editingItem}
                onCancelEdit={() => setEditingItem(null)}
                onSaved={(message) => {
                  setSnackbar({ open: true, message, severity: 'success' });
                  loadDetail();
                }}
                onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
              />
            )}

            {status === 'PREPARATION' && consignmentId && items.length > 0 && (
              <Box mt={2}>
                <Button variant="contained" fullWidth onClick={handleOpenAllocate}>
                  {translate('consignment.allocate_to_client')}
                </Button>
              </Box>
            )}

            {status === 'ACTIVE' && (
              <Box mt={2} display="flex" flexDirection="column" gap={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setCloseDialog({ open: true, mode: 'renew' })}
                >
                  {translate('consignment.renew_consignment')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  onClick={() => setCloseDialog({ open: true, mode: 'finalize' })}
                >
                  {translate('consignment.finalize_consignment')}
                </Button>
              </Box>
            )}

            {(status === 'ACTIVE' || isClosed) && changeLog.length > 0 && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {translate('consignment.change_log_title')}
                </Typography>
                <ConsignmentChangeLogTimeline entries={changeLog} />
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            {consignmentId && (
              <ConsignmentItemsTable
                consignmentId={consignmentId}
                items={items}
                status={status}
                onEditItem={setEditingItem}
                onRefresh={loadDetail}
                onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
              />
            )}
          </Grid>
        </Grid>

        <ConsignmentAllocateDialog
          open={allocateOpen}
          onClose={() => setAllocateOpen(false)}
          onConfirm={handleAllocate}
          loading={actionLoading}
        />

        <ConsignmentCloseDialog
          open={closeDialog.open}
          mode={closeDialog.mode}
          items={items}
          onClose={() => setCloseDialog({ open: false, mode: closeDialog.mode })}
          onConfirm={handleCloseCycle}
          loading={actionLoading}
        />

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ConsignmentSettingsGuard>
  );
}

export default ConsignmentPage;
